import { QBox, QIconButton, QMenuButton } from '@qualio/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MigrationFile } from '../../../api/model/migration';

export interface UploadedFileRowActionsProps {
  uploadedFile: MigrationFile;
  isFirst: boolean;
  isLast: boolean;
  handleOrderChange: (file: MigrationFile, direction: string) => void;
  removeHandler: (fileToRemove: MigrationFile) => void;
  editHandler: (file: MigrationFile) => void;
  disableActions: boolean;
}

// Keep action IDs as constants
export const FileActions = {
  EDIT: 'EDIT',
  DELETE: 'DELETE',
} as const;

export const UploadedFileRowActions = ({
  uploadedFile,
  isFirst,
  isLast,
  handleOrderChange,
  removeHandler,
  editHandler,
  disableActions,
}: UploadedFileRowActionsProps) => {
  const { t } = useTranslation();

  const fileActionItems = useMemo(
    () => [
      {
        id: FileActions.EDIT,
        label: t('buttons.edit'),
      },
      {
        id: FileActions.DELETE,
        label: t('buttons.delete'),
      },
    ],
    [t],
  );

  const handleMoveUp = () => {
    handleOrderChange(uploadedFile, 'up');
  };

  const handleMoveDown = () => {
    handleOrderChange(uploadedFile, 'down');
  };

  const actionHandlerMap: {
    [actionId: string]: (file: MigrationFile) => void;
  } = useMemo(
    () => ({
      [FileActions.DELETE]: removeHandler,
      [FileActions.EDIT]: editHandler,
    }),
    [editHandler, removeHandler],
  );

  return (
    <QBox>
      <QIconButton
        iconName="ArrowUp"
        aria-label="moveUp"
        onClick={handleMoveUp}
        isDisabled={isFirst || disableActions}
        data-cy="moveUp"
      />
      <QIconButton
        iconName="ArrowDown"
        aria-label="moveDown"
        onClick={handleMoveDown}
        isDisabled={isLast || disableActions}
        data-cy="moveDown"
      />
      <QMenuButton
        variant="icon"
        buttonLabel="options"
        iconName="MoreVertical"
        items={fileActionItems}
        onItemClick={({ id: actionId }) => {
          const actionHandler = actionHandlerMap[actionId];
          actionHandler(uploadedFile);
        }}
        isDisabled={disableActions}
        data-cy="moreOptions"
      />
    </QBox>
  );
};
